import React from 'react';
import { snapTo90Degrees } from '../utils/geometry';

/**
 * ShapeDrawingTool - Component for drawing freeform shapes
 * 
 * @param {Object} props
 * @param {Array} props.shapePoints - Array of points in the shape
 * @param {boolean} props.isClosingShape - Whether the shape is being closed
 * @param {Function} props.transformPoint - Function to transform points (not used when SVG is in transformed container)
 * @param {number} props.zoom - Current zoom level (for scaling elements)
 * @param {boolean} props.shiftPressed - Whether the shift key is currently pressed
 * @param {Object} props.currentMousePos - Current mouse position for drawing preview
 * @returns {JSX.Element} ShapeDrawingTool component
 */
const ShapeDrawingTool = ({ 
  shapePoints, 
  isClosingShape, 
  transformPoint, 
  zoom = 1, 
  shiftPressed = false,
  currentMousePos = null
}) => {
  if (!shapePoints || shapePoints.length === 0) {
    return null;
  }

  // Generate preview line to current mouse position if available
  const showPreview = currentMousePos && shapePoints.length > 0;
  let previewEndPoint = currentMousePos;
  
  // If shift is pressed, snap the preview line to 90 degrees
  if (showPreview && shiftPressed && currentMousePos) {
    const lastPoint = shapePoints[shapePoints.length - 1];
    previewEndPoint = snapTo90Degrees(lastPoint, currentMousePos);
  }

  return (
    <g className="shape-drawing-tool">
      {/* Draw lines connecting points */}
      {shapePoints.map((point, index) => {
        if (index === 0) return null;
        const prevPoint = shapePoints[index - 1];
        return (
          <line
            key={`line-${index}`}
            x1={prevPoint.x}
            y1={prevPoint.y}
            x2={point.x}
            y2={point.y}
            stroke="blue"
            strokeWidth={2 / zoom}
          />
        );
      })}

      {/* Draw closing line if shape is being closed */}
      {isClosingShape && shapePoints.length >= 3 && (
        <line
          x1={shapePoints[shapePoints.length - 1].x}
          y1={shapePoints[shapePoints.length - 1].y}
          x2={shapePoints[0].x}
          y2={shapePoints[0].y}
          stroke="green"
          strokeWidth={2 / zoom}
          strokeDasharray={`${5 / zoom},${5 / zoom}`}
        />
      )}

      {/* Draw preview line to current mouse position */}
      {showPreview && !isClosingShape && (
        <line
          x1={shapePoints[shapePoints.length - 1].x}
          y1={shapePoints[shapePoints.length - 1].y}
          x2={previewEndPoint.x}
          y2={previewEndPoint.y}
          stroke="blue"
          strokeWidth={2 / zoom}
          strokeDasharray={`${5 / zoom},${5 / zoom}`}
        />
      )}

      {/* Draw points */}
      {shapePoints.map((point, index) => (
        <circle
          key={`point-${index}`}
          cx={point.x}
          cy={point.y}
          r={index === 0 && isClosingShape ? 8 / zoom : 5 / zoom}
          fill={index === 0 ? (isClosingShape ? "green" : "blue") : "blue"}
          stroke="white"
          strokeWidth={2 / zoom}
        />
      ))}
    </g>
  );
};

export default ShapeDrawingTool; 